import React, { useRef, useState, useEffect } from "react";
import { Bell, LogOut, Menu, UserCircle, X, ChevronDown } from "lucide-react";

type NavbarProps = {
  sidebarToggle: boolean;
  setSidebarToggle: React.Dispatch<React.SetStateAction<boolean>>;
  onLogout: () => void;
};

const Navbar: React.FC<NavbarProps> = ({
  sidebarToggle,
  setSidebarToggle,
  onLogout,
}) => {
  const [dropdownOpen, setDropdownOpen] = useState(false);
  const dropdownRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    const handler = (e: MouseEvent) => {
      if (
        dropdownRef.current &&
        !dropdownRef.current.contains(e.target as Node)
      ) {
        setDropdownOpen(false);
      }
    };

    document.addEventListener("mousedown", handler);
    return () => document.removeEventListener("mousedown", handler);
  }, []);

  return (
    <div
      className={`fixed top-0 right-0 z-20 transition-all duration-300 ${
        sidebarToggle ? "left-0" : "left-72"
      }`}
    >
      {/* 🔥 Green Navbar matching app theme */}
      <nav className="bg-gradient-to-r from-green-600 to-green-700 px-6 py-4 shadow-lg border-b border-green-500/20">
        <div className="flex justify-between items-center">
          {/* Sidebar Toggle Button */}
          <button
            onClick={() => setSidebarToggle(!sidebarToggle)}
            className="p-2.5 rounded-xl bg-white/10 hover:bg-white/20 transition-all duration-200 text-white shadow-md backdrop-blur-sm"
          >
            {sidebarToggle ? (
              <Menu className="w-5 h-5" />
            ) : (
              <X className="w-5 h-5" />
            )}
          </button>

          {/* Right Side */}
          <div className="flex items-center gap-3">
            {/* Notifications */}
            <button className="relative p-2.5 rounded-xl bg-white/10 hover:bg-white/20 transition-all duration-200 text-white shadow-md backdrop-blur-sm">
              <Bell className="w-5 h-5" />
              <span className="absolute top-1.5 right-1.5 w-2 h-2 bg-red-500 rounded-full ring-2 ring-green-600 animate-pulse" />
            </button>

            {/* User Profile */}
            <div className="relative" ref={dropdownRef}>
              <button
                onClick={() => setDropdownOpen(!dropdownOpen)}
                className="flex items-center gap-3 px-4 py-2 rounded-xl bg-white/10 hover:bg-white/20 transition-all duration-200 shadow-md backdrop-blur-sm border border-white/10"
              >
                <div className="w-9 h-9 bg-white/20 rounded-lg flex items-center justify-center">
                  <UserCircle className="w-6 h-6 text-white" />
                </div>

                <span className="text-sm font-medium text-white hidden md:block">
                  Admin
                </span>

                <ChevronDown
                  className={`w-4 h-4 text-white/70 transition-transform duration-200 ${
                    dropdownOpen ? "rotate-180" : ""
                  }`}
                />
              </button>

              {/* Dropdown Menu */}
              {dropdownOpen && (
                <div className="absolute right-0 mt-3 w-64 bg-white rounded-2xl shadow-2xl border border-gray-100 overflow-hidden animate-fadeIn">
                  <div className="p-4 border-b border-gray-100 bg-gradient-to-r from-green-50 to-green-100/50">
                    <div className="flex items-center gap-3">
                      <div className="w-12 h-12 bg-green-600 rounded-xl flex items-center justify-center text-white font-bold text-xl">
                        A
                      </div>
                      <div>
                        <p className="text-sm font-semibold text-gray-900">
                          Admin Account
                        </p>
                        <p className="text-xs text-gray-500 mt-0.5">
                          admin@whatbox.com
                        </p>
                      </div>
                    </div>
                  </div>

                  <div className="p-2">
                    <button
                      onClick={onLogout}
                      className="w-full flex items-center gap-3 px-4 py-3 rounded-xl hover:bg-red-50 text-red-600 hover:text-red-700 transition duration-200"
                    >
                      <div className="w-9 h-9 bg-red-100 rounded-lg flex items-center justify-center">
                        <LogOut className="w-4 h-4" />
                      </div>
                      <span className="text-sm font-medium">Log Out</span>
                    </button>
                  </div>
                </div>
              )}
            </div>
          </div>
        </div>
      </nav>

      {/* Add animation styles */}
      <style>{`
        @keyframes fadeIn {
          from {
            opacity: 0;
            transform: translateY(-10px) scale(0.95);
          }
          to {
            opacity: 1;
            transform: translateY(0) scale(1);
          }
        }
        .animate-fadeIn {
          animation: fadeIn 0.2s ease-out;
        }
      `}</style>
    </div>
  );
};

export default Navbar;
